import type { BooleanInput } from '@angular/cdk/coercion';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import type { ChangeFn, TouchFn } from '@spartan-ng/brain/forms';
import { BrnSwitch, type BrnSwitchSize, BrnSwitchThumb } from '@spartan-ng/brain/switch';
import { hlm } from '@lite-workspace/shared/helm-ui/utils';
import { HlmSwitchThumb } from './hlm-switch-thumb';

export const HLM_SWITCH_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => HlmSwitch),
  multi: true,
};

let nextHlmSwitchId = 0;

@Component({
  selector: 'hlm-switch',
  imports: [BrnSwitchThumb, BrnSwitch, HlmSwitchThumb],
  providers: [HLM_SWITCH_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'data-slot': 'switch',
    class: 'contents',
    '[attr.aria-label]': 'null',
    '[attr.aria-labelledby]': 'null',
    '[attr.aria-describedby]': 'null',
  },
  template: `
    <brn-switch
      [class]="_computedClass()"
      [size]="size()"
      [checked]="checked()"
      (checkedChange)="handleChange($event)"
      (touched)="_onTouched?.()"
      [disabled]="_disabled()"
      [id]="resolvedId()"
      [aria-label]="ariaLabel()"
      [aria-labelledby]="ariaLabelledby()"
      [aria-describedby]="ariaDescribedby()"
    >
      <brn-switch-thumb hlm />
    </brn-switch>
  `,
})
export class HlmSwitch implements ControlValueAccessor {
  protected readonly _computedClass = computed(() =>
    hlm(
      'data-checked:bg-primary data-unchecked:bg-line3 focus-visible:border-ring focus-visible:ring-ring ' +
        'focus-visible:ring-offset-1 focus-visible:ring-offset-panel ' +
        'data-[matches-spartan-invalid=true]:ring-destructive ' +
        'data-[matches-spartan-invalid=true]:border-destructive ' +
        'dark:data-[matches-spartan-invalid=true]:border-destructive/50 ' +
        'rounded-full border border-transparent shadow-xs focus-visible:ring-1 ' +
        'data-[matches-spartan-invalid=true]:ring-1 data-[size=default]:h-[var(--switch-track-h)] ' +
        'data-[size=default]:w-8 data-[size=sm]:h-3.5 data-[size=sm]:w-6 group/switch inline-flex shrink-0 ' +
        'items-center transition-all outline-none data-[disabled=true]:cursor-not-allowed ' +
        'data-[disabled=true]:opacity-50',
    ),
  );

  /** The checked state of the switch. */
  public readonly checkedInput = input<boolean, BooleanInput>(false, { alias: 'checked', transform: booleanAttribute });
  public readonly checked = linkedSignal(this.checkedInput);

  /** Emits when the checked state of the switch changes. */
  public readonly checkedChange = output<boolean>();

  /** The disabled state of the switch. */
  public readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  /** The size of the switch. */
  public readonly size = input<BrnSwitchSize>('default');

  /** Used to set the id on the underlying brn element. */
  public readonly inputId = input<string | null>(null);

  // Без id brn-switch штампует каждому обёрнутому <label> одинаковый id "null-label",
  // и все свитчи страницы резолвят accessible name в первый такой label.
  private readonly generatedId = `hlm-switch-${nextHlmSwitchId++}`;
  protected readonly resolvedId = computed(() => this.inputId() ?? this.generatedId);

  /** Used to set the aria-label attribute on the underlying brn element. */
  public readonly ariaLabel = input<string | null>(null, { alias: 'aria-label' });

  /** Used to set the aria-labelledby attribute on the underlying brn element. */
  public readonly ariaLabelledby = input<string | null>(null, { alias: 'aria-labelledby' });

  /** Used to set the aria-describedby attribute on the underlying brn element. */
  public readonly ariaDescribedby = input<string | null>(null, { alias: 'aria-describedby' });

  protected readonly _disabled = linkedSignal(this.disabled);

  protected _onChange?: ChangeFn<boolean>;
  protected _onTouched?: TouchFn;

  protected handleChange(value: boolean): void {
    this.checked.set(value);
    this._onChange?.(value);
    this.checkedChange.emit(value);
  }

  /** CONTROL VALUE ACCESSOR */
  writeValue(value: boolean): void {
    this.checked.set(Boolean(value));
  }

  registerOnChange(fn: ChangeFn<boolean>): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: TouchFn): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled.set(isDisabled);
  }
}
